import { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { LayoutDashboard, Users, Mail, LogOut, Menu, X } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AdminLayout({ children }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [admin, setAdmin] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    const token = localStorage.getItem('admin_token');
    if (!token) {
      navigate('/admin/login');
      return;
    }

    try {
      const response = await axios.get(`${API}/admin/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAdmin(response.data);
    } catch (error) {
      localStorage.removeItem('admin_token');
      navigate('/admin/login');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    navigate('/admin/login');
  };

  const navLinks = [
    { path: '/admin', label: 'Dashboard', icon: <LayoutDashboard size={20} /> },
    { path: '/admin/talents', label: 'Talents', icon: <Users size={20} /> },
    { path: '/admin/contacts', label: 'Messages', icon: <Mail size={20} /> },
  ];

  if (!admin) {
    return (
      <div className="loading" style={{ minHeight: '100vh' }}>
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="admin-layout" data-testid="admin-layout">
      {/* Mobile Header */}
      <div className="admin-mobile-header" style={{
        display: 'none',
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 40,
        backgroundColor: 'var(--surface)',
        padding: '1rem',
        borderBottom: '1px solid rgba(255,255,255,0.1)',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        <span style={{ color: 'var(--primary)', fontWeight: 700, fontFamily: 'Playfair Display, serif' }}>
          W&A Admin
        </span>
        <button 
          onClick={() => setSidebarOpen(!sidebarOpen)}
          className="btn btn-ghost"
          data-testid="mobile-menu-toggle"
        >
          {sidebarOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {/* Sidebar */}
      <aside className={`admin-sidebar ${sidebarOpen ? 'open' : ''}`} data-testid="admin-sidebar">
        <div style={{ marginBottom: '2rem' }}>
          <Link to="/" style={{ 
            color: 'var(--primary)', 
            fontWeight: 700, 
            fontSize: '1.25rem',
            fontFamily: 'Playfair Display, serif',
            textDecoration: 'none'
          }}>
            Woodhouse & Archer
          </Link>
          <p style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginTop: '0.25rem' }}>
            Admin Dashboard
          </p>
        </div>

        <nav>
          {navLinks.map((link) => (
            <Link
              key={link.path}
              to={link.path}
              className={`admin-nav-link ${location.pathname === link.path ? 'active' : ''}`}
              onClick={() => setSidebarOpen(false)}
              data-testid={`admin-nav-${link.label.toLowerCase()}`}
            >
              {link.icon}
              {link.label}
            </Link>
          ))}
        </nav>

        <div style={{ marginTop: 'auto', paddingTop: '2rem', borderTop: '1px solid rgba(255,255,255,0.1)' }}>
          <p style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', marginBottom: '1rem' }}>
            Logged in as<br />
            <strong style={{ color: 'var(--text-primary)' }}>{admin.name || admin.email}</strong>
          </p>
          <button 
            onClick={handleLogout}
            className="admin-nav-link"
            style={{ width: '100%', border: 'none', cursor: 'pointer', background: 'none' }}
            data-testid="logout-btn"
          >
            <LogOut size={20} />
            Logout
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="admin-main">
        {children}
      </main>

      <style>{`
        @media (max-width: 768px) {
          .admin-mobile-header {
            display: flex !important;
          }
          .admin-sidebar {
            position: fixed;
            top: 60px;
            left: 0;
            bottom: 0;
            transform: translateX(-100%);
            transition: transform 0.3s ease;
            z-index: 30;
          }
          .admin-sidebar.open {
            transform: translateX(0);
          }
          .admin-main {
            margin-left: 0 !important;
            padding-top: 80px !important;
          }
        }
      `}</style>
    </div>
  );
}
